import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Package, Truck, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["placed", "confirmed", "packed", "shipped", "delivered"];
const STATUS_ICONS = { placed: Package, confirmed: CheckCircle2, packed: Package, shipped: Truck, delivered: MapPin };

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`, { withCredentials: true }).then(r => setOrder(r.data)).catch(() => {});
  }, [orderId]);

  if (!order) return <div className="max-w-4xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4"><div className="h-8 bg-slate-200 rounded w-1/3 mx-auto" /><div className="h-40 bg-slate-200 rounded-2xl" /></div></div>;

  const statusIdx = STATUSES.indexOf(order.status);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="order-confirmation-page">
      <div className="text-center mb-8 animate-fadeUp">
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
        </div>
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Order Placed!</h1>
        <p className="text-slate-500">Thank you for your order. Your order ID is <strong className="text-cyan-600">{order.order_id}</strong></p>
      </div>

      {/* Status Timeline */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 mb-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-6">Order Status</h3>
        <div className="flex items-center justify-between relative">
          <div className="absolute top-4 left-0 right-0 h-0.5 bg-slate-200"><div className="h-full bg-cyan-600 transition-all" style={{ width: `${(statusIdx / (STATUSES.length - 1)) * 100}%` }} /></div>
          {STATUSES.map((s, i) => {
            const Icon = STATUS_ICONS[s];
            return (
              <div key={s} className="relative flex flex-col items-center z-10">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${i <= statusIdx ? 'bg-cyan-600 text-white' : 'bg-slate-200 text-slate-400'}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className={`text-[10px] mt-2 font-medium capitalize ${i <= statusIdx ? 'text-cyan-600' : 'text-slate-400'}`}>{s}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Order Details */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 mb-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">Order Details</h3>
        <div className="space-y-3">
          {order.items?.map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <img src={item.image} alt={item.name} className="w-12 h-12 rounded-xl object-cover bg-slate-50" />
              <div className="flex-1"><p className="text-sm font-medium text-slate-800">{item.name}</p><p className="text-xs text-slate-500">Qty: {item.quantity}</p></div>
              <span className="text-sm font-semibold">Rs.{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 mt-4 pt-4 space-y-1 text-sm">
          <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>Rs.{order.subtotal}</span></div>
          <div className="flex justify-between text-slate-600"><span>Delivery</span><span>{order.delivery_charges === 0 ? "FREE" : `Rs.${order.delivery_charges}`}</span></div>
          <div className="flex justify-between text-slate-600"><span>Tax</span><span>Rs.{order.tax}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount</span><span>-Rs.{order.discount}</span></div>}
          <div className="flex justify-between font-bold text-slate-800 text-base pt-2 border-t"><span>Total</span><span>Rs.{order.total}</span></div>
        </div>
      </div>

      <div className="flex gap-3 justify-center">
        <Link to="/account/orders"><Button variant="outline" className="rounded-full border-cyan-200 text-cyan-700" data-testid="track-order-btn">Track Order</Button></Link>
        <Link to="/products"><Button className="rounded-full bg-cyan-600 hover:bg-cyan-700" data-testid="continue-shopping-btn">Continue Shopping <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
      </div>
    </div>
  );
}
